"use client";

import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@workspace/ui/components/accordion";

type Platform = "common" | "ios" | "android";

type FAQItem = { question: string; platform: Platform; paragraphs: string[] };
type FAQCategory = { name: string; items: FAQItem[] };
type FAQData = { title: string; categories: FAQCategory[] };

export default function FAQ({
  data,
  platform = "common",
}: {
  data: FAQData;
  platform?: Platform;
}) {
  const categories = useMemo(() => {
    const filtered = (data?.categories ?? []).map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (it) => it.platform === "common" || it.platform === platform
      ),
    }));
    return filtered.filter((c) => c.items.length > 0);
  }, [data, platform]);

  const first = categories[0]?.name ?? "";
  const [active, setActive] = useState(slugify(first));
  const [openFaqSlug, setOpenFaqSlug] = useState<string | undefined>(
    undefined,
  );
  const activeCat =
    categories.find((c) => slugify(c.name) === active) ?? categories[0];

  useEffect(() => {
    setOpenFaqSlug(undefined);
  }, [active]);

  const panelId = `faq-panel-${active}`;
  const getTabId = (slug: string, idx: number) => `faq-tab-${idx + 1}`;

  return (
    <section
      className="mx-auto w-full max-w-[1280px] px-6 md:px-8 py-12 md:py-16"
      id="faq-section"
      data-testid="faq-section">
      {data?.title && (
        <h1
          className="mb-8 md:text-center text-[40px] leading-[48px] tracking-[-0.5px] font-medium text-[#232221] md:text-[56px] md:leading-[66px]"
          id="faq-heading"
          data-testid="faq-heading">
          {data.title}
        </h1>
      )}

      <div
        className="mx-auto w-full max-w-[748px]"
        id="faq-body"
        data-testid="faq-body">
        {categories.length > 0 && (
          <div
            role="tablist"
            aria-label="FAQ categories"
            aria-orientation="horizontal"
            className="relative"
            id="faq-tabs"
            data-testid="faq-tabs">
            <div
              className="rounded-[8px] bg-[#F4F4F5]"
              id="faq-tabs-surface"
              data-testid="faq-tabs-surface">
              <div
                className="flex w-full overflow-x-auto overflow-y-hidden md:overflow-visible rounded-[8px] no-scrollbar"
                id="faq-tabs-scroll"
                data-testid="faq-tabs-scroll">
                <div
                  className="flex w-max md:w-full"
                  id="faq-tabs-list"
                  data-testid="faq-tabs-list">
                  {categories.map((cat, idx) => {
                    const v = slugify(cat.name);
                    const isActive = v === active;
                    const tabId = getTabId(v, idx);
                    return (
                      <button
                        key={v}
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setActive(v)}
                        className={`relative flex-none md:flex-1 rounded-xl px-4 py-3 text-[18px] leading-7 tracking-[-0.5px] font-medium transition ${isActive ? " text-[#232221] " : "text-[#6B7280]"}`}
                        aria-controls={panelId}
                        id={`faq-tab-${idx + 1}`}
                        data-testid={`faq-tab-${idx + 1}`}>
                        {cat.name}
                        <span
                          className={`pointer-events-none absolute left-0 right-0 -bottom-[2px] h-[4px] md:h-[2px] rounded-full ${isActive ? "bg-black" : "bg-transparent"}`}
                          id={`faq-tab-${idx + 1}-underline`}
                          data-testid={`faq-tab-${idx + 1}-underline`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeCat && (
          <div
            className="mt-8"
            id="faq-accordion-block"
            data-testid="faq-accordion-block"
            role="tabpanel"
            aria-labelledby={getTabId(
              slugify(activeCat.name),
              categories.findIndex((c) => c.name === activeCat.name)
            )}
            aria-live="polite"
            aria-describedby={data?.title ? "faq-heading" : undefined}>
            <Accordion
              type="single"
              collapsible
              className="w-full"
              id="faq-accordion"
              data-testid="faq-accordion"
              value={openFaqSlug}
              onValueChange={(value) => {
                setOpenFaqSlug(value);
                if (!value || !activeCat) return;
                const opened = activeCat.items.find(
                  (i) => slugify(i.question) === value,
                );
                if (!opened) return;
                sendEvent(
                  EventNames.FAQ_QUESTION_OPENED,
                  withSectionType(SectionTypes.FAQ, {
                    question: opened.question,
                    category: activeCat.name,
                  }),
                );
              }}
            >
              {activeCat.items.map((item, idx) => {
                const qslug = slugify(item.question);
                const isLast = idx === activeCat.items.length - 1;
                return (
                  <AccordionItem
                    key={qslug}
                    value={qslug}
                    className={`border-b border-b-[var(--Divider,#EDEDED)] ${isLast ? "last:border-b-0" : ""}`}
                    id={`faq-accordion-item-${idx + 1}`}
                    data-testid={`faq-accordion-item-${idx + 1}`}>
                    <AccordionTrigger
                      className="py-8 text-left text-[18px] tracking-[-0.5px] leading-[28px] font-medium text-[#112D35] md:text-[20px] md:leading-[30px]"
                      id={`faq-accordion-item-${idx + 1}-trigger`}
                      data-testid={`faq-accordion-item-${idx + 1}-trigger`}>
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent
                      className="pb-4"
                      id={`faq-accordion-item-${idx + 1}-content`}
                      data-testid={`faq-accordion-item-${idx + 1}-content`}>
                      <div
                        className="space-y-3"
                        id={`faq-accordion-item-${idx + 1}-content-stack`}
                        data-testid={`faq-accordion-item-${idx + 1}-content-stack`}>
                        {item.paragraphs.map((p, pIdx) => (
                          <p
                            key={pIdx}
                            className="m-0 text-[15px] leading-[24px] tracking-[-0.2px] text-[#112D35B2]"
                            id={`faq-accordion-item-${idx + 1}-paragraph-${pIdx + 1}`}
                            data-testid={`faq-accordion-item-${idx + 1}-paragraph-${pIdx + 1}`}>
                            {parse(p)}
                          </p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
    </section>
  );
}

function slugify(input: string) {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}
